
import React from 'react';
import { IntegrationFlow, Language } from '../../types';
import FlowsList from './FlowsList';
import FlowsHeader from './FlowsHeader';
import FlowsLibrary from './FlowsLibrary';
import FlowsCanvas from './FlowsCanvas';
import FlowsInspector from './FlowsInspector';

interface FlowsComponentProps {
  language: Language;
  flows: IntegrationFlow[];
  activeFlow: IntegrationFlow | null;
  viewMode: 'list' | 'editor';
  isLibraryOpen: boolean;
  libraryTab: 'nodes' | 'services';
  isSimulating: boolean;
  pan: { x: number; y: number };
  zoom: number;
  selectedNodeId: string | null;
  showInspector: boolean;
  connectionStart: string | null;
  mousePos: { x: number; y: number };
  canvasRef: React.RefObject<HTMLDivElement>;
  
  // Actions
  onAddFlow: () => void;
  onOpenFlow: (flow: IntegrationFlow) => void;
  onSave: () => void;
  onDeleteFlow: (id: string) => void;
  onAutoLayout: () => void;
  onToggleSimulation: () => void;
  onUpdateFlowName: (name: string) => void;
  onSetViewMode: (mode: 'list' | 'editor') => void;
  onSetLibraryTab: (tab: 'nodes' | 'services') => void;
  onAddNode: (def: any, isService: boolean) => void;
  onDeleteNode: (id: string) => void;
  onNodeMouseDown: (e: React.MouseEvent, nodeId: string) => void;
  onCanvasMouseDown: (e: React.MouseEvent | React.TouchEvent) => void;
  onPortMouseDown: (e: React.MouseEvent, nodeId: string) => void;
  onPortMouseUp: (e: React.MouseEvent, nodeId: string) => void;
  onUpdateNodeLabel: (val: string) => void;
  onUpdateNodeConfig: (key: string, value: any) => void;
  onCloseInspector: () => void;
  onZoom: (delta: number) => void;
  onResetView: () => void;
}

const FlowsComponent: React.FC<FlowsComponentProps> = (props) => {
  const { 
    language, flows, activeFlow, viewMode, isLibraryOpen, libraryTab, isSimulating,
    pan, zoom, selectedNodeId, showInspector, connectionStart, mousePos, canvasRef,
    onAddFlow, onOpenFlow, onSave, onDeleteFlow, onAutoLayout, onToggleSimulation,
    onUpdateFlowName, onSetViewMode, onSetLibraryTab, onAddNode, onDeleteNode,
    onNodeMouseDown, onCanvasMouseDown, onPortMouseDown, onPortMouseUp, onUpdateNodeLabel,
    onUpdateNodeConfig, onCloseInspector, onZoom, onResetView
  } = props;

  if (viewMode === 'list' || !activeFlow) {
    return (
      <FlowsList 
        language={language}
        flows={flows}
        onOpenFlow={onOpenFlow}
        onAddFlow={onAddFlow}
        onDeleteFlow={onDeleteFlow}
      />
    );
  }

  // Editor View
  const selectedNode = activeFlow.nodes.find(n => n.id === selectedNodeId);

  return (
    <div className="fixed inset-0 z-[60] bg-[#0f0f13] text-slate-200 flex flex-col font-sans">
        <FlowsHeader 
          language={language}
          activeFlow={activeFlow}
          isSimulating={isSimulating}
          onSetViewMode={onSetViewMode}
          onUpdateFlowName={onUpdateFlowName}
          onAutoLayout={onAutoLayout}
          onToggleSimulation={onToggleSimulation}
          onSave={onSave}
        />

        <div className="flex-1 relative flex overflow-hidden">
          <FlowsLibrary 
            language={language}
            isLibraryOpen={isLibraryOpen}
            libraryTab={libraryTab}
            onSetLibraryTab={onSetLibraryTab}
            onAddNode={onAddNode}
          />

          <FlowsCanvas 
            language={language}
            activeFlow={activeFlow}
            isSimulating={isSimulating}
            pan={pan}
            zoom={zoom}
            selectedNodeId={selectedNodeId}
            connectionStart={connectionStart}
            mousePos={mousePos}
            canvasRef={canvasRef}
            onNodeMouseDown={onNodeMouseDown}
            onCanvasMouseDown={onCanvasMouseDown}
            onPortMouseDown={onPortMouseDown}
            onPortMouseUp={onPortMouseUp}
            onZoom={onZoom}
            onResetView={onResetView}
          />

          <FlowsInspector 
            language={language}
            showInspector={showInspector}
            selectedNode={selectedNode}
            onUpdateNodeLabel={onUpdateNodeLabel}
            onUpdateNodeConfig={onUpdateNodeConfig}
            onCloseInspector={onCloseInspector}
            onDeleteNode={onDeleteNode}
          />
        </div>
    </div>
  );
};

export default FlowsComponent;
